import { AnyAction, combineReducers, configureStore, Reducer } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import { Persistor, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { RESET_STATE_ACTION_TYPE } from './actions/resetState';
import { loginReducer } from './features/login.slice';
import { LayoutReducer } from './slices/layouts/reducer';
import { userListingReducer } from './features/user-listing.slice';
import { productReducer } from './features/product.slice';
import { cartReducer } from './features/cart.slice';
import { orderReducer } from './features/order.slice';
import { categoryReducer } from './features/category.slice';
import { tierReducer } from './features/tier.slice';
import { dosageReducer } from './features/dosage.slice';
import { warehouseReducer } from './features/warehouse.slice';
import { manufacturerReducer } from './features/manufacturer.slice';
import { supplierReducer } from './features/supplier.slice';
import { offerReducer } from './features/offer.slice';
import { notificationReducer } from './features/notification.slice';
import { batchReducer } from './features/batch.slice';
import { inventoryManagementReducer } from './features/inventory-management.slice';
import { traceabilityReducer } from './features/traceability.slice';
import { transactionStatementsReducer } from './features/transaction-statements.slice';
import { productLifeCycleReducer } from './features/product-lifecycle.slice';
import { auditReducer } from './features/audit.slice';
import { epcisReducer } from './features/epcis.slice';
import { sftpIntegrationReducer } from './features/sftp-integration.slice';
import { as2IntegrationReducer } from './features/as2-integration.slice';
import { bullkReducer } from './features/bullk.slice';
import { analyticsReducer } from './features/analytics.slice';
import { commentReducer } from './features/comment.slice';
import { alertReducer } from './features/alert.slice';
import { mapReducer } from './features/map.slice';
import { returnReducer } from './features/return.slice';
import { shipmentReducer } from './features/shipment.slice';
import { contactReducer } from './features/contact.slice';
import { backorderReducer } from './features/backorder.slice';

// ---------------------------------------------------------------------------------------------------------------------
const persistConfig = {
    key: `root`,
    storage,
};
// ---------------------------------------------------------------------------------------------------------------------
export const combinedReducers: Reducer = combineReducers({
    authState: loginReducer,
    Layout: LayoutReducer,
    userListingState: userListingReducer,
    productState: productReducer,
    cartState: cartReducer,
    orderState: orderReducer,
    categoryState: categoryReducer,
    tierState: tierReducer,
    dosageState: dosageReducer,
    warehouseState: warehouseReducer,
    manufacturerState: manufacturerReducer,
    supplierState: supplierReducer,
    offerState: offerReducer,
    notificationState: notificationReducer,
    batchState: batchReducer,
    inventoryManagementState: inventoryManagementReducer,
    traceabilityState: traceabilityReducer,
    transactionStatementsState: transactionStatementsReducer,
    productLifeCycleState: productLifeCycleReducer,
    auditState: auditReducer,
    epcisState: epcisReducer,
    sftpIntegrationState: sftpIntegrationReducer,
    as2IntegrationState: as2IntegrationReducer,
    bullkState: bullkReducer,
    analyticsState: analyticsReducer,
    commentState: commentReducer,
    alertState: alertReducer,
    mapState: mapReducer,
    returnState: returnReducer,
    shipmentState: shipmentReducer,
    contactState: contactReducer,
    backorderState: backorderReducer,
});
// ---------------------------------------------------------------------------------------------------------------------
export type RootState = ReturnType<typeof combinedReducers>;
// ---------------------------------------------------------------------------------------------------------------------
const rootReducer: Reducer<RootState> = (state: RootState, action: AnyAction) => {
    if (action.type === RESET_STATE_ACTION_TYPE) {
        state = undefined;
    }
    return combinedReducers(state, action);
};
// ---------------------------------------------------------------------------------------------------------------------
const persistedReducer: Reducer = persistReducer<any, any>(persistConfig, rootReducer);
// ---------------------------------------------------------------------------------------------------------------------
export const setupStore = () =>
    configureStore({
        reducer: persistedReducer,
    });
// ---------------------------------------------------------------------------------------------------------------------
export const store = setupStore();
export type AppStore = ReturnType<typeof setupStore>;
setupListeners(store.dispatch);
// ---------------------------------------------------------------------------------------------------------------------
export const persistor: Persistor = persistStore(store);
